import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Save, Coins, Percent, Image as ImageIcon, Palette } from "lucide-react";
import { toast } from "sonner";
import ImageInput from "../components/ImageInput";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => api.get("/admin/settings").then((r) => setSettings(r.data));
  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!settings) return;
    setBusy(true);
    try {
      await api.patch("/admin/settings", {
        platform_fee_issue_pct: parseFloat(settings.platform_fee_issue_pct),
        platform_fee_redeem_pct: parseFloat(settings.platform_fee_redeem_pct),
        default_cashback_rate_pct: parseFloat(settings.default_cashback_rate_pct),
        checkout_card_fee_pct: parseFloat(settings.checkout_card_fee_pct ?? 4.99),
        checkout_pix_fee_pct: parseFloat(settings.checkout_pix_fee_pct ?? 0.99),
        checkout_take_rate_pct: parseFloat(settings.checkout_take_rate_pct ?? 8),
        cashback_validity_days: parseInt(settings.cashback_validity_days ?? 90, 10),
        home_sections_order: settings.home_sections_order,
        discover_by_segments_enabled: !!settings.discover_by_segments_enabled,
        brand_logo_url: settings.brand_logo_url || "",
        brand_favicon_url: settings.brand_favicon_url || "",
        brand_pwa_icon_url: settings.brand_pwa_icon_url || "",
        brand_short_name: settings.brand_short_name || "MorarBem",
        brand_full_name: settings.brand_full_name || "Morar Bem · Guia + Cashback",
      });
      toast.success("Configurações salvas");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!settings) return <div className="p-10 text-stone-500">Carregando...</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Taxas e Economia do Cashback</h1>
          <p className="text-stone-500 text-sm mt-1">Controle das taxas da plataforma sobre as transações de cashback.</p>
        </div>
        <a href="/admin" className="text-sm text-blue-900 hover:text-blue-950">← Voltar</a>
      </div>

      <Card className="p-5 mb-5 rounded-2xl border-blue-200 bg-blue-50 flex items-start gap-3">
        <Coins className="text-blue-900 shrink-0 mt-0.5" size={20}/>
        <div className="text-sm text-blue-950 space-y-2">
          <p><strong>Como funciona o fluxo financeiro:</strong></p>
          <ol className="list-decimal ml-5 space-y-1 text-xs">
            <li>O cliente compra na Empresa A por R$ 100. A empresa define cashback de 5% = R$ 5.</li>
            <li>A Empresa A paga ao admin via Mercado Pago: <code>R$ 5 (cashback) + taxa emissão %</code>.</li>
            <li>Webhook confirma → R$ 5 caem na carteira do cliente.</li>
            <li>Quando o cliente usa esses R$ 5 na Empresa B, o admin repassa para a Empresa B descontando a taxa de resgate %.</li>
            <li>O admin retém as duas taxas como receita da plataforma e segurança do float.</li>
          </ol>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200 mb-5" data-testid="brand-identity-card">
        <div className="flex items-center gap-2 mb-2">
          <Palette className="text-blue-900" size={18}/>
          <h2 className="font-display text-xl font-bold text-stone-900">Identidade Visual</h2>
        </div>
        <p className="text-xs text-stone-500 mb-4">Logo e favicon exibidos em todo o sistema. Aceita URL ou upload (PNG/SVG).</p>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="flex items-center gap-1.5"><ImageIcon size={13}/> Logo do Sistema (PNG/SVG)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Exibida no header e em e-mails. Proporção recomendada: 4:1 (ex.: 400×100px).</p>
            <ImageInput
              value={settings.brand_logo_url || ""}
              onChange={(v) => setSettings({ ...settings, brand_logo_url: v })}
              placeholder="https://... ou faça upload"
              aspect="4/1"
              testid="brand-logo-input"
            />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><ImageIcon size={13}/> Favicon (ICO/PNG quadrado)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Ícone da aba do navegador. Quadrado, ex.: 64×64 ou 128×128px.</p>
            <ImageInput
              value={settings.brand_favicon_url || ""}
              onChange={(v) => setSettings({ ...settings, brand_favicon_url: v })}
              placeholder="https://... ou faça upload"
              aspect="1/1"
              testid="brand-favicon-input"
            />
          </div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1.5"><ImageIcon size={13}/> Ícone do App Instalado / PWA (PNG quadrado 512×512)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Aparece quando o usuário <strong>instala o app</strong> no celular ("Add to Home Screen") e como apple-touch-icon. Recomendado PNG 512×512 com fundo opaco.</p>
            <ImageInput
              value={settings.brand_pwa_icon_url || ""}
              onChange={(v) => setSettings({ ...settings, brand_pwa_icon_url: v })}
              placeholder="https://... ou faça upload"
              aspect="1/1"
              testid="brand-pwa-icon-input"
            />
          </div>
          <div>
            <Label>Nome curto (short_name)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Aparece abaixo do ícone na tela inicial.</p>
            <input
              value={settings.brand_short_name || ""}
              onChange={(e) => setSettings({ ...settings, brand_short_name: e.target.value })}
              placeholder="MorarBem"
              maxLength={12}
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30"
              data-testid="brand-short-name-input"
            />
          </div>
          <div>
            <Label>Nome completo (title)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Título da aba e nome no app store.</p>
            <input
              value={settings.brand_full_name || ""}
              onChange={(e) => setSettings({ ...settings, brand_full_name: e.target.value })}
              placeholder="Morar Bem · Guia + Cashback"
              className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-900/30"
              data-testid="brand-full-name-input"
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-stone-200">
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <Label className="flex items-center gap-1.5"><Percent size={13}/> Taxa de emissão (%)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Cobrada da empresa quando ela concede cashback.</p>
            <Input type="number" step="0.1" min="0" value={settings.platform_fee_issue_pct}
              onChange={(e) => setSettings({...settings, platform_fee_issue_pct: e.target.value})}
              className="rounded-xl" data-testid="set-fee-issue"/>
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Percent size={13}/> Taxa de resgate (%)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Retida ao repassar valor para a empresa que aceitou o cashback.</p>
            <Input type="number" step="0.1" min="0" value={settings.platform_fee_redeem_pct}
              onChange={(e) => setSettings({...settings, platform_fee_redeem_pct: e.target.value})}
              className="rounded-xl" data-testid="set-fee-redeem"/>
          </div>
          <div className="sm:col-span-2">
            <Label className="flex items-center gap-1.5"><Coins size={13}/> Cashback padrão para novas empresas (%)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Cada empresa pode personalizar depois.</p>
            <Input type="number" step="0.1" min="0" value={settings.default_cashback_rate_pct}
              onChange={(e) => setSettings({...settings, default_cashback_rate_pct: e.target.value})}
              className="rounded-xl" data-testid="set-default-rate"/>
          </div>

          <div className="sm:col-span-2 mt-4 pt-4 border-t border-stone-200">
            <h3 className="font-display text-base font-bold text-stone-900 mb-1">⚡ Arquitetura de Rede</h3>
            <p className="text-[11px] text-stone-500 mb-3">Taxas e parâmetros do sistema de cashback de rede.</p>
          </div>

          <div>
            <Label>Take Rate sobre Vendas Diretas (%)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Split que a plataforma retém no checkout (Venda Direta + Shop).</p>
            <Input type="number" step="0.1" min="0" value={settings.checkout_take_rate_pct ?? 8}
              onChange={(e) => setSettings({...settings, checkout_take_rate_pct: e.target.value})}
              className="rounded-xl" data-testid="set-take-rate"/>
          </div>

          <div>
            <Label>Validade do Cashback (dias)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Após esse prazo, o cashback expira e vira lucro da plataforma (Breakage).</p>
            <Input type="number" step="1" min="1" value={settings.cashback_validity_days ?? 90}
              onChange={(e) => setSettings({...settings, cashback_validity_days: e.target.value})}
              className="rounded-xl" data-testid="set-validity-days"/>
          </div>

          <div>
            <Label>Taxa MP — Cartão (%)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Repassada ao cliente no checkout.</p>
            <Input type="number" step="0.01" min="0" value={settings.checkout_card_fee_pct ?? 4.99}
              onChange={(e) => setSettings({...settings, checkout_card_fee_pct: e.target.value})}
              className="rounded-xl" data-testid="set-card-fee"/>
          </div>

          <div>
            <Label>Taxa MP — PIX (%)</Label>
            <p className="text-[11px] text-stone-500 mt-0.5 mb-1.5">Repassada ao cliente no checkout.</p>
            <Input type="number" step="0.01" min="0" value={settings.checkout_pix_fee_pct ?? 0.99}
              onChange={(e) => setSettings({...settings, checkout_pix_fee_pct: e.target.value})}
              className="rounded-xl" data-testid="set-pix-fee"/>
          </div>
        </div>
        <div className="flex justify-end mt-6">
          <Button onClick={save} disabled={busy} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="set-save">
            <Save size={14} className="mr-1.5"/> {busy ? "Salvando..." : "Salvar configurações"}
          </Button>
        </div>
      </Card>
    </div>
  );
}
